'use client';

import { ColorType, createChart, createImageWatermark, IChartApi, LineData, LineSeries } from 'lightweight-charts';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import keys from 'lodash/keys';
import map from 'lodash/map';
import max from 'lodash/max';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ChipLegend, ChipLegendItem } from './chip-legend';
import { VertLine } from './trading-view-vertical-line';

// ---- TypeScript interfaces ----
interface TradeDataItem {
  date?: string;
  forwarded_date?: string;
  original_date?: string | null;
  label?: string;
  value: number;
  year?: number;
  percentile?: number | null;
  formattedDate?: string;
}

interface TradeData {
  watchlist_id?: number;
  group_id?: number | null;
  index?: number;
  short_name?: string | null;
  formula?: string;
  unit_override?: string | null;
  id?: number;
  data: TradeDataItem[];
}

interface PercentileChartProps {
  tradeData?: TradeData | null;
  loading?: boolean;
}

// Mid-grey used for the current-value/percentile guide lines — same value as
// the 5yr-avg stat band in seasonality.tsx, so a grey reference line reads
// the same "context, not data" role across charts.
const GUIDE_LINE_COLOR = '#6b7280';

// ---- Data processing helpers ----
function processPercentileData (tradeData: TradeData | null): {
  yearData: Record<number, TradeDataItem[]>;
  availableYears: number[];
  hasForwardedDate: boolean;
} {
  if (!tradeData || !isArray(tradeData.data)) {
    return {
      yearData: {},
      availableYears: [],
      hasForwardedDate: false,
    };
  }

  // Check if data has forwarded_date column
  const hasForwardedDate = tradeData.data.length > 0 && tradeData.data[0].forwarded_date !== undefined;

  if (hasForwardedDate) {
    // Use year column directly and filter out invalid years (1, 0, -5)
    const dataWithYears = filter(tradeData.data, (item) =>
      isNumber(item.value) && isNumber(item.year) && item.year !== 1 && item.year !== 0 && item.year !== -5
    );

    // Group by year
    const grouped = groupBy(dataWithYears, 'year');
    const availableYears = sortBy(map(keys(grouped), Number), (year) => -year);

    return {
      yearData: grouped as unknown as Record<number, TradeDataItem[]>,
      availableYears,
      hasForwardedDate: true,
    };
  }
  // Original logic: Filter data to only include items with date and value
  const dataWithDates = filter(tradeData.data, (item) =>
    item.date && isNumber(item.value)
  );

  // Extract year from date and group by year
  const dataWithYears = map(dataWithDates, (item: TradeDataItem) => {
    const date = new Date(item.date || '');
    const year = date.getFullYear();
    return {
      ...item,
      year,
    } as TradeDataItem & { year: number };
  });

  // Group by year
  const grouped = groupBy(dataWithYears, 'year');
  const availableYears = sortBy(map(keys(grouped), Number), (year) => -year);

  return {
    yearData: grouped as unknown as Record<number, TradeDataItem[]>,
    availableYears,
    hasForwardedDate: false,
  };

}

// Compute cumulative distribution from selected years
function computeCumulativeDistribution (
  yearData: Record<number, TradeDataItem[]>,
  selectedYears: number[]
): LineData[] {
  // Collect all values from selected years
  const allValues: number[] = [];

  forEach(selectedYears, year => {
    if (yearData[year]) {
      forEach(yearData[year], item => {
        if (isNumber(item.value)) {
          allValues.push(item.value);
        }
      });
    }
  });

  if (allValues.length === 0) {
    return [];
  }

  // Sort all values
  const sortedValues = sortBy(allValues);
  const totalCount = sortedValues.length;

  // Create chart data with computed percentiles
  return map(sortedValues, (value, index) => {
    // Percentile calculation: (rank / total) where rank goes from 0 to total-1
    const percentile = totalCount > 1 ? index / (totalCount - 1) : 0.5;

    return {
      time: percentile as any,
      value,
    };
  });
}

export default function PercentileChart ({ tradeData, loading = false }: PercentileChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);

  // Process trade data
  const { yearData, availableYears, hasForwardedDate } = useMemo(() =>
    processPercentileData(tradeData || null), [tradeData]);

  // Initialize selected years with the latest 3 available years
  const [selectedYears, setSelectedYears] = useState<number[]>([]);

  // Update selected years when availableYears changes
  useEffect(() => {
    if (availableYears.length > 0) {
      const latestThreeYears = availableYears.slice(0, 3);
      setSelectedYears(latestThreeYears);
    }
  }, [availableYears]);

  const toggleYearSelection = (year: number) => {
    setSelectedYears(prev =>
      includes(prev, year)
        ? filter(prev, y => y !== year)
        : [...prev, year].sort((a, b) => b - a) // Sort descending
    );
  };

  // Compute cumulative distribution for selected years
  const cumulativeData = useMemo(() =>
    computeCumulativeDistribution(yearData, selectedYears),
  [yearData, selectedYears]
  );

  // Calculate current value and percentile
  const { currentValue, currentPercentile } = useMemo(() => {
    if (!tradeData || !isArray(tradeData.data) || tradeData.data.length === 0) {
      return { currentValue: null, currentPercentile: null };
    }

    let mostRecentItem: TradeDataItem | undefined;

    if (hasForwardedDate) {
      // For forwarded data, find the item with the latest original_date
      const validItems: TradeDataItem[] = filter(tradeData.data, item =>
        item.original_date && isNumber(item.value) && isNumber(item.year)
      ) as TradeDataItem[];

      if (validItems.length === 0) {
        return { currentValue: null, currentPercentile: null };
      }

      // Find the latest year using the year property
      const years = map(validItems, (item: TradeDataItem) => item.year as number);
      const latestYear = max(years);

      if (latestYear === undefined) {
        return { currentValue: null, currentPercentile: null };
      }

      // Filter to only items from the latest year
      const itemsFromLatestYear = filter(validItems, {'year': latestYear});

      if (itemsFromLatestYear.length === 0) {
        return { currentValue: null, currentPercentile: null };
      }

      // Sort by original_date descending and take the first
      const sortedItems: TradeDataItem[] = sortBy(itemsFromLatestYear, (item: TradeDataItem) => {
        const date = new Date(item.original_date || '');
        return -date.getTime(); // Negative for descending order
      }) as TradeDataItem[];

      mostRecentItem = sortedItems[0];
    } else {
      // Original logic: Get the most recent data point (data is sorted in descending order by date)
      mostRecentItem = tradeData.data[0];
    }

    if (!mostRecentItem || !isNumber(mostRecentItem.value)) {
      return { currentValue: null, currentPercentile: null };
    }

    const currentValue = mostRecentItem.value;

    // Calculate percentile by finding where this value falls in the cumulative distribution
    let currentPercentile = 0;
    if (cumulativeData.length > 0) {
      // Find the closest data point in the cumulative distribution
      let closestIndex = 0;
      let minDiff = Math.abs(cumulativeData[0].value - currentValue);

      for (let i = 1; i < cumulativeData.length; i++) {
        const diff = Math.abs(cumulativeData[i].value - currentValue);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = i;
        }
      }

      currentPercentile = cumulativeData[closestIndex].time as number;
    }

    return { currentValue, currentPercentile };
  }, [tradeData, cumulativeData, hasForwardedDate]);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      rightPriceScale: {
        visible: true,
        borderVisible: true,
      },
      leftPriceScale: { visible: false },
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#222',
      },
      grid: {
        vertLines: { visible: true, color: '#e6e6e6' },
        horzLines: { visible: true, color: '#e6e6e6' },
      },
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      timeScale: {
        rightOffset: 0,
        fixLeftEdge: true,
        fixRightEdge: true,
        allowBoldLabels: false,
        tickMarkFormatter: (percentile: number) => {
          return `${(percentile * 100).toFixed(0)}%`;
        },
      },
    });

    chartRef.current = chart;

    // Add watermark
    const img = new window.Image();
    img.src = '/assets/hilo-watermark.png';
    img.onload = () => {
      // Guard against chart being destroyed before image loads
      const pane = chart.panes()?.[0];
      if (!pane) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        createImageWatermark(pane, dataUrl, {
          alpha: 0.05,
          padding: 20,
        });
      }
    };

    // Create a single series for the cumulative distribution
    const series = chart.addSeries(LineSeries, {
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      lineWidth: 2,
      color: '#00a99d',
    });

    // Set the cumulative distribution data
    series.setData(cumulativeData);

    // Add current value and percentile lines if available
    if (currentValue !== null && currentPercentile !== null) {
      // Add horizontal line for current value
      series.createPriceLine({
        price: currentValue,
        color: GUIDE_LINE_COLOR,
        lineWidth: 1,
        lineStyle: 2, // Dashed line
        axisLabelVisible: true,
        title: `Current Value: ${currentValue.toFixed(2)}`,
      });

      // Add vertical line using the VertLine plugin
      const vertLine = new VertLine(chart, series, currentPercentile as any, {
        showLabel: true,
        labelText: `Percentile: ${(currentPercentile * 100).toFixed(1)}%`,
        color: GUIDE_LINE_COLOR,
        width: 1,
        labelBackgroundColor: GUIDE_LINE_COLOR,
        labelTextColor: 'white',
      });
      series.attachPrimitive(vertLine);
    }

    // Crosshair move handler for custom tooltip
    const handleCrosshairMove = (param: any) => {
      if (param.point === undefined || !isNumber(param.time) || param.point.x < 0 || param.point.y < 0) {
        return;
      }

      const percentile = param.time as number;
      const percentileStr = `${(percentile * 100).toFixed(1)}%`;

      // Get the value at this percentile
      const seriesData = param.seriesData.get(series) as LineData;

      if (!seriesData || !isNumber(seriesData.value)) {
        return;
      }

      // Create tooltip content
      let tooltipContent = '<div style="background: white; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); font-family: Arial, sans-serif; font-size: 12px;">';
      tooltipContent += `<div style="font-weight: bold; margin-bottom: 4px;">Percentile: ${percentileStr}</div>`;
      tooltipContent += `<div>Value: <strong>${seriesData.value.toFixed(2)}</strong></div>`;
      tooltipContent += '</div>';

      // Update tooltip position and content
      const tooltip = document.getElementById('percentile-tooltip');
      if (tooltip && containerRef.current) {
        tooltip.innerHTML = tooltipContent;
        tooltip.style.display = 'block';

        // Get container width to determine if cursor is in right portion
        const containerWidth = containerRef.current.clientWidth;
        const cursorX = param.point.x;
        const isInRightHalf = cursorX > containerWidth * 0.5;

        // Position tooltip to the left if cursor is in right half of chart
        if (isInRightHalf) {
          // Force a reflow to get accurate tooltip width
          void tooltip.offsetWidth;
          const tooltipWidth = tooltip.getBoundingClientRect().width || 200;
          const leftPosition = cursorX - tooltipWidth - 10;
          // Ensure tooltip doesn't go off the left edge
          tooltip.style.left = `${Math.max(10, leftPosition)}px`;
        } else {
          tooltip.style.left = `${cursorX + 10}px`;
        }

        tooltip.style.top = `${param.point.y - 10}px`;
      }
    };

    // Subscribe to crosshair move to show custom tooltip
    chart.subscribeCrosshairMove(handleCrosshairMove);

    // Handle mouse leave/enter
    const containerEl = containerRef.current;
    const handleMouseLeave = () => {
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Percentile chart: unable to unsubscribe crosshair move on mouse leave', error);
      }
      const tooltip = document.getElementById('percentile-tooltip');
      if (tooltip) {
        tooltip.style.display = 'none';
      }
    };

    const handleMouseEnter = () => {
      chart.subscribeCrosshairMove(handleCrosshairMove);
    };

    containerEl?.addEventListener('mouseleave', handleMouseLeave);
    containerEl?.addEventListener('mouseenter', handleMouseEnter);

    // Set visible range to show 0-100% (0-1 in decimal)
    // Only set if there's data to display
    if (cumulativeData.length > 0) {
      chart.timeScale().setVisibleRange({
        from: 0 as any,
        to: 1 as any,
      });
    }

    return () => {
      containerEl?.removeEventListener('mouseleave', handleMouseLeave);
      containerEl?.removeEventListener('mouseenter', handleMouseEnter);
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (error) {
        console.warn('Percentile chart: unable to unsubscribe crosshair move during cleanup', error);
      }
      chart.remove();
    };
  }, [cumulativeData, selectedYears, currentValue, currentPercentile]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        Loading...
      </div>
    );
  }

  // Show empty state if no data
  if (!tradeData || !isArray(tradeData.data) || tradeData.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        No data available
      </div>
    );
  }

  // Show message if no percentile data available
  if (availableYears.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">
        No percentile data available
      </div>
    );
  }

  const legendItems: ChipLegendItem[] = map(availableYears, (year) => ({
    key: String(year),
    label: String(year),
    color: '#00a99d',
    hidden: !includes(selectedYears, year),
  }));

  return (
    <div className="h-full w-full flex flex-col">
      {/* Legend */}
      <div className="py-2 px-4 border-b min-h-14 flex justify-center">
        <ChipLegend
          items={legendItems}
          onToggle={(key) => toggleYearSelection(Number(key))}
        />
      </div>

      {/* Chart */}
      <div className="flex-grow min-h-0 min-w-0 relative overflow-hidden" ref={containerRef}>
        {/* Custom tooltip */}
        <div
          id="percentile-tooltip"
          style={{
            position: 'absolute',
            display: 'none',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}
